<template>
  <div class="app-breadcrumb-wrap">
    <nav class="app-breadcrumb" :aria-label="ariaLabel">
      <template v-for="(item, index) in resolvedItems" :key="`${item.label}-${index}`">
        <router-link
          v-if="item.to"
          :to="item.to"
          class="app-breadcrumb__link"
        >
          {{ item.label }}
        </router-link>

        <a
          v-else-if="item.href"
          :href="item.href"
          class="app-breadcrumb__link"
        >
          {{ item.label }}
        </a>

        <span v-else class="app-breadcrumb__text">{{ item.label }}</span>

        <span
          v-if="index < resolvedItems.length - 1"
          class="app-breadcrumb__separator"
          aria-hidden="true"
        >
          {{ separator }}
        </span>
      </template>
    </nav>

    <div v-if="showBack" class="app-breadcrumb-back-row">
      <button
        type="button"
        class="app-breadcrumb-back-btn"
        @click="handleBack"
      >
        <i class="fa fa-arrow-left" aria-hidden="true"></i>
        <span>{{ backLabel }}</span>
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AppBreadcrumb',
  emits: ['back'],
  props: {
    mode: {
      type: String,
      default: 'manual',
    },
    items: {
      type: Array,
      default: () => [],
    },
    ariaLabel: {
      type: String,
      default: 'Breadcrumb',
    },
    separator: {
      type: String,
      default: '/',
    },
    showBack: {
      type: Boolean,
      default: false,
    },
    backLabel: {
      type: String,
      default: 'Volver',
    },
    source: {
      type: String,
      default: '',
    },
    contextKey: {
      type: String,
      default: '',
    },
    contextId: {
      type: [String, Number],
      default: '',
    },
    reference: {
      type: String,
      default: '',
    },
    labels: {
      type: Object,
      default: () => ({}),
    },
    sourceTargets: {
      type: Object,
      default: () => ({}),
    },
    backTarget: {
      type: [Object, String],
      default: null,
    },
    fallbackTarget: {
      type: [Object, String],
      default: null,
    },
    useHistoryBack: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    normalizedManualItems() {
      return (Array.isArray(this.items) ? this.items : [])
        .map((item) => {
          if (typeof item === 'string') {
            return { label: item.trim(), to: null, href: '' };
          }

          if (!item || typeof item !== 'object') {
            return null;
          }

          return {
            label: String(item.label || '').trim(),
            to: item.to || null,
            href: String(item.href || '').trim(),
          };
        })
        .filter((item) => item && item.label !== '');
    },
    isInvoiceDetailMode() {
      return String(this.mode || '').trim().toLowerCase() === 'invoice-detail';
    },
    normalizedContextId() {
      return String(this.contextId || '').trim();
    },
    contextPayload() {
      if (this.contextKey === '') {
        return {};
      }

      try {
        const raw = sessionStorage.getItem(this.contextKey);
        const parsed = raw ? JSON.parse(raw) : null;
        const payload = parsed && typeof parsed === 'object' ? parsed : {};
        const storedId = String(payload?.contextId || '').trim();

        if (this.normalizedContextId !== '' && storedId !== '' && storedId !== this.normalizedContextId) {
          return {};
        }

        return payload;
      } catch (error) {
        return {};
      }
    },
    normalizedSource() {
      const explicit = String(this.source || '').trim().toLowerCase();
      if (explicit !== '') {
        return explicit;
      }

      return String(this.contextPayload?.source || '').trim().toLowerCase();
    },
    invoiceLabels() {
      const source = this.labels && typeof this.labels === 'object' ? this.labels : {};

      return {
        invoiceWord: String(source.invoiceWord || 'Factura').trim() || 'Factura',
        invoicesListRoot: String(source.invoicesListRoot || 'Facturas').trim() || 'Facturas',
        salesRoot: String(source.salesRoot || 'Negocios').trim() || 'Negocios',
        customersRoot: String(source.customersRoot || 'Clientes').trim() || 'Clientes',
        billingRoot: String(source.billingRoot || 'Facturación').trim() || 'Facturación',
        invoicesRoot: String(source.invoicesRoot || 'Todas las facturas').trim() || 'Todas las facturas',
        saleFallback: String(source.saleFallback || 'Negocio').trim() || 'Negocio',
        customerFallback: String(source.customerFallback || 'Cliente').trim() || 'Cliente',
      };
    },
    invoiceDetailItems() {
      const reference = String(this.reference || '').trim() || '-';
      const invoiceReference = `${this.invoiceLabels.invoiceWord} ${reference}`;
      const source = this.normalizedSource;
      const saleId = Number(this.contextPayload?.saleId || 0) || 0;
      const customerId = Number(this.contextPayload?.customerId || 0) || 0;
      const saleName = String(this.contextPayload?.saleName || '').trim() || this.invoiceLabels.saleFallback;
      const customerName = String(this.contextPayload?.customerName || '').trim() || this.invoiceLabels.customerFallback;

      if (source === 'invoices-list' || source === 'invoices') {
        return [
          { label: this.invoiceLabels.invoicesListRoot, to: { name: 'invoices' } },
          { label: invoiceReference },
        ];
      }

      if ((source === 'sale-detail' || source === 'sales') && saleId > 0) {
        return [
          { label: this.invoiceLabels.salesRoot, to: { name: 'sales' } },
          { label: saleName, to: { name: 'saleDetail', params: { saleId: String(saleId) } } },
          { label: invoiceReference },
        ];
      }

      if ((source === 'customer-detail' || source === 'customers') && customerId > 0) {
        return [
          { label: this.invoiceLabels.customersRoot, to: { name: 'customers' } },
          { label: customerName, to: { name: 'customerDetail', params: { customerId: String(customerId) } } },
          { label: invoiceReference },
        ];
      }

      return [
        { label: this.invoiceLabels.billingRoot, to: { name: 'invoices' } },
        { label: this.invoiceLabels.invoicesRoot, to: { name: 'invoices' } },
        { label: reference },
      ];
    },
    resolvedItems() {
      const baseItems = this.isInvoiceDetailMode ? this.invoiceDetailItems : this.normalizedManualItems;
      const lastIndex = baseItems.length - 1;

      return baseItems.map((item, index) => {
        if (index !== lastIndex) {
          return item;
        }

        return {
          ...item,
          to: null,
          href: '',
        };
      });
    },
    resolvedSourceTarget() {
      if (this.isInvoiceDetailMode) {
        const saleId = Number(this.contextPayload?.saleId || 0) || 0;
        const customerId = Number(this.contextPayload?.customerId || 0) || 0;

        if (this.normalizedSource === 'invoices-list' || this.normalizedSource === 'invoices') {
          return { name: 'invoices' };
        }

        if ((this.normalizedSource === 'sale-detail' || this.normalizedSource === 'sales') && saleId > 0) {
          return { name: 'saleDetail', params: { saleId: String(saleId) } };
        }

        if ((this.normalizedSource === 'customer-detail' || this.normalizedSource === 'customers') && customerId > 0) {
          return { name: 'customerDetail', params: { customerId: String(customerId) } };
        }
      }

      if (this.normalizedSource === '') {
        return null;
      }

      const targets = this.sourceTargets && typeof this.sourceTargets === 'object'
        ? this.sourceTargets
        : {};

      return targets[this.normalizedSource] || null;
    },
  },
  methods: {
    pushTarget(target) {
      if (!target || !this.$router) {
        return false;
      }

      this.$router.push(target);
      return true;
    },
    handleBack() {
      this.$emit('back', {
        source: this.normalizedSource,
      });

      if (this.pushTarget(this.backTarget)) {
        return;
      }

      if (this.pushTarget(this.resolvedSourceTarget)) {
        return;
      }

      if (this.useHistoryBack && this.$router && typeof window !== 'undefined' && window.history.length > 1) {
        this.$router.back();
        return;
      }

      if (this.pushTarget(this.fallbackTarget)) {
        return;
      }
    },
  },
};
</script>

<style scoped>
.app-breadcrumb-wrap {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.app-breadcrumb {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
}

.app-breadcrumb__link,
.app-breadcrumb__text {
  color: inherit;
  text-decoration: none;
}

.app-breadcrumb__separator {
  color: #9ca3af;
}

.app-breadcrumb-back-row {
  display: flex;
  align-items: center;
}

.app-breadcrumb-back-btn {
  border: 0;
  background: transparent;
  color: #334155;
  font-size: 12px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 0;
}

.app-breadcrumb-back-btn:hover,
.app-breadcrumb-back-btn:focus {
  color: #0070cd;
}
</style>
